import { useCallback, useEffect, useState } from "react"
import { Activity, AlertCircle, RefreshCw } from "lucide-react"
import { apiGet, API_ENDPOINTS } from "../config/api"
import { useLanguage } from "../i18n/LanguageContext"

const ACTIONS = ["", "admin_login", "user_status_changed", "user_governorate_changed", "captain_status_changed", "captain_approved", "captain_rejected", "captain_activated", "captain_deactivated", "notification_sent", "notification_retry", "receipt_retry"]

export default function AuditLog() {
  const { t } = useLanguage()
  const [records, setRecords] = useState([])
  const [action, setAction] = useState("")
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true)
    else setLoading(true)
    try {
      const suffix = action ? `?limit=200&action=${encodeURIComponent(action)}` : "?limit=200"
      const response = await apiGet(`${API_ENDPOINTS.ADMIN_AUDIT_LOG}${suffix}`)
      setRecords(response.records || [])
      setError("")
    } catch (requestError) {
      setError(requestError.message || t("audit.loadFailed"))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [action, t])

  useEffect(() => {
    load()
    const timer = window.setInterval(() => load(true), 20000)
    return () => window.clearInterval(timer)
  }, [load])

  return (
    <section className="audit-page">
      <div className="management-header">
        <div>
          <h2 className="management-title">{t("audit.title")}</h2>
          <p className="text-gray-500 mt-1">{t("audit.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select className="border border-gray-200 rounded-lg bg-white px-3 py-2 text-sm" value={action} onChange={(event) => setAction(event.target.value)} aria-label={t("audit.filter")}>{ACTIONS.map((value) => <option key={value} value={value}>{value ? t(`audit.actions.${value}`) : t("audit.allActions")}</option>)}</select>
          <button type="button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50" onClick={() => load(true)} disabled={refreshing}><RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />{t("audit.refresh")}</button>
        </div>
      </div>

      {error && <div className="p-4 mb-5 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center gap-2"><AlertCircle className="w-5 h-5" />{error}</div>}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        {loading ? <div className="p-8 text-center text-gray-500">{t("common.loading")}</div> : records.length === 0 ? <div className="p-10 text-center text-gray-500"><Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />{t("audit.empty")}</div> : <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50 border-b border-gray-200"><tr>{["time", "administrator", "action", "resource", "details", "ip"].map((key) => <th key={key} className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap">{t(`audit.${key}`)}</th>)}</tr></thead><tbody className="divide-y divide-gray-200">{records.map((record) => <tr key={record.event_id} className="hover:bg-gray-50 align-top"><td className="px-4 py-3 whitespace-nowrap text-gray-600">{record.created_at ? new Date(record.created_at).toLocaleString() : t("common.notAvailable")}</td><td className="px-4 py-3"><div className="font-medium text-gray-800">{record.admin_name || t("common.notAvailable")}</div>{record.admin_id != null && <div className="text-xs text-gray-500">#{record.admin_id}</div>}</td><td className="px-4 py-3 whitespace-nowrap"><span className="inline-flex rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-semibold">{t(`audit.actions.${record.action}`)}</span></td><td className="px-4 py-3 text-gray-600">{record.resource_type || "—"}{record.resource_id != null ? ` #${record.resource_id}` : ""}</td><td className="px-4 py-3 text-gray-600 max-w-sm">{record.details && Object.keys(record.details).length ? Object.entries(record.details).map(([key, value]) => <div key={key}><span className="font-medium">{key}:</span> {String(value)}</div>) : "—"}</td><td className="px-4 py-3 whitespace-nowrap text-gray-600">{record.ip_address || t("common.notAvailable")}</td></tr>)}</tbody></table></div>}
      </div>
    </section>
  )
}
